import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, HStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import React, { forwardRef } from 'react';

export type DisplayCardProps = BoxProps & {
  usd?: ReactNode;
  inputChips?: ReactNode;
  error?: ReactNode;
  warn?: ReactNode;
  tokenSelector?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  addonRight?: ReactNode;
};

export const DisplayCard = forwardRef<HTMLDivElement, DisplayCardProps>(
  (
    {
      children,
      usd,
      inputChips,
      error,
      warn,
      tokenSelector,
      readOnly,
      disabled,
      addonRight,
      ...props
    },
    ref,
  ) => {
    const isDisabled = readOnly || disabled;
    const hasAddonRight = !!addonRight || !!tokenSelector;

    return (
      <Box {...props}>
        <Flex
          alignItems="stretch"
          rounded="8px"
          bg={isDisabled ? 'state.disabledBg' : 'white'}
          _focusWithin={
            isDisabled
              ? undefined
              : {
                  boxShadow: error
                    ? '2px 2px 0px 0px {colors.state.error}'
                    : '2px 2px 0px 0px {colors.text.default}',
                }
          }
        >
          <Flex
            ref={ref}
            flex="1"
            flexDir="column"
            minH="72px"
            px={{
              base: '16px',
              desktop: '24px',
            }}
            py="8px"
            borderWidth="2px"
            borderRightWidth={hasAddonRight ? '0' : undefined}
            borderColor={
              error && !isDisabled ? 'state.error' : 'text.secondary'
            }
            _focusWithin={
              isDisabled
                ? undefined
                : {
                    borderColor: error ? 'state.error' : 'text.default',
                  }
            }
            roundedLeft="8px"
            roundedRight={hasAddonRight ? '0px' : '8px'}
          >
            <Box
              textStyle="body1Bold"
              my="auto"
              color={isDisabled ? 'text.secondary' : 'currentcolor'}
            >
              {children}
            </Box>
            <HStack hidden={!error && !warn && !usd}>
              <Box wordBreak="break-word">
                {!error && usd && (
                  <Box
                    textStyle="body3"
                    color={isDisabled ? 'text.secondary' : 'currentcolor'}
                  >
                    {usd}
                  </Box>
                )}
                {error && (
                  <Box
                    textStyle="body3"
                    color="state.error"
                    _firstLetter={{
                      textTransform: 'capitalize',
                    }}
                  >
                    {error}
                  </Box>
                )}
                {warn && (
                  <Box
                    textStyle="body3"
                    color="state.warn"
                    _firstLetter={{
                      textTransform: 'capitalize',
                    }}
                  >
                    {warn}
                  </Box>
                )}
              </Box>
            </HStack>
          </Flex>
          {tokenSelector && (
            <Flex
              w="110px"
              flexShrink="0"
              alignItems="stretch"
              justify="stretch"
              ml="-1px"
            >
              {tokenSelector}
            </Flex>
          )}
          {addonRight && (
            <Flex
              flexShrink="0"
              alignItems="stretch"
              justify="stretch"
              ml="-1px"
            >
              {addonRight}
            </Flex>
          )}
        </Flex>
        {inputChips && <Box mt="8px">{inputChips}</Box>}
      </Box>
    );
  },
);

DisplayCard.displayName = 'DisplayCard';
